/**
 * Browser-side mirror of the agent output-format contract in
 * `backend/do-chat-agents/do_chat_agents/output_format.py`.
 *
 * One source of truth for two consumers: the scrubber-compatibility test
 * (a convention silently stripped before render is indistinguishable from the
 * model ignoring it) and, later, the block-inference rules. Separators are
 * matched loosely — hyphen, en dash, em dash and middle dot — because that is
 * the character models substitute most freely while still following the shape.
 */

export const STATUS_VALUES = ['open', 'blocked', 'done', 'unclear']

const SEP = '[-–—·]'

/** `- **Title** - detail [status: open]` */
export const SIGNAL_ITEM_RE = new RegExp(
  `^[-*]\\s+\\*\\*(.+?)\\*\\*\\s*(?:${SEP}\\s*)?(.*?)\\s*\\[status:\\s*(${STATUS_VALUES.join('|')})\\s*\\]\\s*$`,
  'i',
)

/**
 * `### Phase 2 - Harden - weeks 3-5`
 *
 * The separator must be *surrounded by spaces*. Without that requirement the
 * intra-word hyphens real output contains — "Blue-green rollout", "weeks 3-5",
 * "Q1 Weeks 1-4" — are indistinguishable from field separators, and the name
 * and timeframe get split at the wrong place. Requiring ` - ` costs nothing:
 * every captured phase heading uses spaced separators.
 */
export const PHASE_HEADING_RE = new RegExp(
  `^###\\s+Phase\\s+(\\d+)\\s+${SEP}\\s+(.+?)\\s+${SEP}\\s+(.+?)\\s*$`,
  'i',
)

/** `| Field | Value |` — the record-summary header row. */
export const RECORD_HEADER_RE = /^\|\s*Field\s*\|\s*Value\s*\|\s*$/i

/**
 * One representative line per contract rule. Every entry must survive
 * `cleanPhase1TextForArtifacts` unchanged — see `phase1ContentDisplay.contract.test.js`.
 */
export const CONTRACT_SAMPLES = [
  { rule: 'heading', line: '## Findings' },
  { rule: 'subheading', line: '### Coverage gaps' },
  { rule: 'table-header', line: '| Region | Accounts | Renewal |' },
  { rule: 'table-divider', line: '| --- | ---: | --- |' },
  { rule: 'table-row', line: '| EMEA | 42 | 2026-09-01 |' },
  { rule: 'signal-item', line: '- **Renewal risk** - three accounts lapse in Q3 [status: open]' },
  { rule: 'phase-heading', line: '### Phase 2 - Harden - weeks 3-5' },
  { rule: 'record-header', line: '| Field | Value |' },
  { rule: 'record-row', line: '| Owner | Priya Raman |' },
]
